import { supabase } from "@/integrations/supabase/client";

/**
 * Resilient lead submission.
 *
 * Layer 1 – try the backend function (saves the lead + sends emails).
 * Layer 2 – if that fails (backend paused, offline, timeout), post the same
 *           payload to an independent backup endpoint outside Lovable Cloud so
 *           the enquiry still reaches the business by email.
 * Layer 3 – always keep a local copy and retry in the background until the
 *           backend confirms it was saved, so nothing is ever lost.
 */

const QUEUE_KEY = "supreme_lead_queue_v1";
const MAX_QUEUE = 50;
const TIMEOUT_MS = 20000;

/** Optional off-Cloud backup form endpoint (e.g. a form-capture service). */
const BACKUP_ENDPOINT: string =
  (import.meta.env.VITE_LEAD_BACKUP_ENDPOINT as string | undefined) || "";

export type LeadPayload = Record<string, unknown> & { type: string; email?: string };

export interface LeadSubmitResult {
  /** True when the customer should see a success screen. */
  ok: boolean;
  /** True when the lead is stored locally / with the backup and will sync later. */
  queued: boolean;
  /** True when the independent backup endpoint accepted the enquiry. */
  backupSent: boolean;
}

interface QueuedLead {
  id: string;
  payload: LeadPayload;
  createdAt: string;
  attempts: number;
}

function readQueue(): QueuedLead[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedLead[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch {
    /* storage unavailable / full – nothing else we can do client-side */
  }
}

function enqueue(payload: LeadPayload) {
  const items = readQueue();
  items.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
  });
  writeQueue(items);
}

/** Error the customer must see: the enquiry was rejected and will not be retried. */
export class LeadRejectedError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "LeadRejectedError";
    this.status = status;
  }
}

/** Read the HTTP status + server message out of a functions.invoke error. */
async function describeInvokeError(error: unknown): Promise<{ status: number; message: string }> {
  const res = (error as { context?: Response })?.context;
  const status = typeof res?.status === "number" ? res.status : 0;
  let message = "";
  try {
    if (res && typeof res.clone === "function") {
      const body = await res.clone().json();
      message = (body?.error as string) || (body?.message as string) || "";
    }
  } catch {
    /* non-JSON body */
  }
  return { status, message };
}

async function callBackend(payload: LeadPayload): Promise<void> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("Request timed out")), TIMEOUT_MS),
  );
  const { error } = (await Promise.race([
    supabase.functions.invoke("send-enquiry-email", { body: payload }),
    timeout,
  ])) as { error: unknown };
  if (!error) return;

  const { status, message } = await describeInvokeError(error);
  // 4xx (except request timeout) means the server refused this enquiry — retrying
  // will never help, so the customer must be told instead of seeing a success screen.
  if (status >= 400 && status < 500 && status !== 408) {
    throw new LeadRejectedError(
      message ||
        (status === 429
          ? "Too many enquiries sent from this device. Please wait a few minutes and try again."
          : "Some of the details entered could not be accepted. Please check your details and try again."),
      status,
    );
  }
  throw error;
}

async function callBackup(payload: LeadPayload): Promise<boolean> {
  if (!BACKUP_ENDPOINT) return false;
  try {
    const res = await fetch(BACKUP_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: "supreme-turf-website",
        capture_source: "backup",
        submitted_at: new Date().toISOString(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
        ...payload,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Submit a lead. Throws LeadRejectedError when the server refuses the enquiry
 * (invalid details, rate limit) so the form shows a real error. Temporary
 * failures (offline, timeout, backend down) resolve as queued.
 */
export async function submitLead(payload: LeadPayload): Promise<LeadSubmitResult> {
  try {
    await callBackend({ ...payload, capture_source: "direct" });
    return { ok: true, queued: false, backupSent: false };
  } catch (err) {
    if (err instanceof LeadRejectedError) throw err;
    console.error("Lead submission via backend failed, using backup path:", err);
    const backupSent = await callBackup(payload);
    enqueue(payload);
    return { ok: true, queued: true, backupSent };
  }
}

/** Retry any locally queued leads. Safe to call often; runs one pass. */
export async function flushLeadQueue(): Promise<void> {
  const items = readQueue();
  if (items.length === 0) return;
  const remaining: QueuedLead[] = [];

  for (const item of items) {
    try {
      await callBackend({
        ...item.payload,
        capture_source: "recovered",
        original_submitted_at: item.createdAt,
      });
    } catch {
      // Give up after many attempts so the queue cannot grow forever; the
      // backup endpoint already delivered these by email.
      if (item.attempts < 20) remaining.push({ ...item, attempts: item.attempts + 1 });
    }
  }
  writeQueue(remaining);
}

/** Number of enquiries still waiting to sync (used for diagnostics). */
export function pendingLeadCount(): number {
  return readQueue().length;
}

let started = false;
/** Start background retries: on load, when back online, and on tab focus. */
export function startLeadQueueSync() {
  if (started || typeof window === "undefined") return;
  started = true;
  const run = () => { void flushLeadQueue(); };
  window.setTimeout(run, 4000);
  window.addEventListener("online", run);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.setInterval(run, 5 * 60 * 1000);
}
